// Angular imports
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

// CC-Common imports
import { IconConstants, Utils } from 'cc-common';

// 3rd party import
import notify from 'devextreme/ui/notify';
import { Subscription } from 'rxjs';

// Component imports
import { LiteModePreferenceComponent } from '../../../shared-module/lite-mode-preference/lite-mode-preference.component';

// Service imports
import { WorkflowVendorEditService } from '../../_services/workflow-vendor-edit.service';
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { HelperService } from '../../../_services/shared-services/helper.service';
import { UserService } from '../../../_services/data-services/user.service';
import { CommunityService } from '../../../_services/data-services/community.service';
import { ContextManagerService } from '../../../_services/data-services/context-manager.service';
import { WorkflowEditorService } from '../../_services/workflow-editor.service';
import { WorkflowSharedService } from '../../../shared-workflow-module/_services/workflow-shared.service';
import { CiraHelpService } from '../../../_services/shared-services/cira-help.service';
import { PrecisionService } from '../../../_services/shared-services/precision.service';

// Model imports
import { WorkflowVendorEditResultDto } from '../../_models/workflow-vendor-edit/workflow-vendor-edit-result-dto';
import { WorkflowVendorEditParametersDto } from '../../_models/workflow-vendor-edit/workflow-vendor-edit-parameters-dto';
import { WorkFlowParametersDto } from '../../../shared-workflow-module/_models/workflow-parameters-dto';
import { CommunitySelectorResultDto } from '../../../_models/community-selector-result-dto';
import { PropertyCommunityInfoResultDto } from '../../../_models/property-community-info-result-dto';
import { WFEDeleteWorkflowParametersDto } from '../../_models/common/wfe-delete-workflow-parameters-dto';
import { StatusMessageResultDto } from '../../../_models/status-message-result-dto';
import { ActionMenuEvent, ActionMenuInfo } from '../../../shared-module/_models/action-menu-info';

// Constant imports
import { MessageInterval } from '../../../_constants/messages-intervals';
import { StringFormatConstants } from '../../../_constants/string-format-constants';
import { CiraHelpKeyConstants } from '../../../_constants/cira-help-key-constants';
import { ActionConstants } from '../../../_constants/action-constants';

@Component({
    selector: 'app-manage-workflow-vendor-edit',
    templateUrl: './manage-workflow-vendor-edit.component.html',
    styleUrls: ['./manage-workflow-vendor-edit.component.scss']
})
export class ManageWorkflowVendorEditComponent implements OnInit, OnDestroy {
    @ViewChild('liteModePreference', { static: false }) liteModePreference: LiteModePreferenceComponent;

    gridSource: WorkflowVendorEditResultDto[] = [];
    selectedWorkflowVendorEditRow: WorkflowVendorEditResultDto;
    selectedContext: PropertyCommunityInfoResultDto;

    communityList: CommunitySelectorResultDto[] = [];
    communityColumns: any[];
    communityGridBoxValue: number[];
    selectedCommunity: CommunitySelectorResultDto;

    isLiteMode = true;
    showUsersListPopup = false;
    actionMenuItems: Array<ActionMenuInfo> = [];
    communityId: number = null;
    currencyFormat = StringFormatConstants.currencyFormat;

    showWorkFlowNotesPopUp = false;
    showWorkFlowHistoryPopUp = false;
    showWorkFlowBoardHistoryPopUp = false;
    showWorkFlowBoardNotesPopUp = false;
    showWorkflowVendorEdit = false;
    showDeleteWorkflowPopUp = false;

    loadingVisible = false;
    isInternalUser = false;
    contextManagerSubscription: Subscription;

    dateFormat = StringFormatConstants.dateFormat;
    iconNotes: string = IconConstants.NOTES_AVAILABLE;
    iconEmptyNotes: string = IconConstants.NOTES_EMPTY;
    iconPDF: string = IconConstants.PDF_FILE_GRID;
    iconHistory: string = IconConstants.HISTORY;
    iconCancel: string = IconConstants.CANCEL;
    iconCheck: string = IconConstants.CHECK;
    iconRefresh: string = IconConstants.REFRESH;
    iconLock: string = IconConstants.LOCK;
    iconEdit: string = IconConstants.EDIT_NEW;
    iconDelete: string = IconConstants.DELETE;

    helpLink: string;
    workflowVendorEditParams: WorkflowVendorEditParametersDto;
    uniqueResponsibleUsers: Array<any> = [];

    constructor(
        private readonly wfVendorEditService: WorkflowVendorEditService,
        private readonly workflowSharedService: WorkflowSharedService,
        private readonly workflowEditorService: WorkflowEditorService,
        private readonly communityService: CommunityService,
        private readonly helperService: HelperService,
        private readonly userService: UserService,
        private readonly errorService: ServerErrorService,
        private readonly contextManagerService: ContextManagerService,
        private readonly ciraHelpService: CiraHelpService,
        private readonly route: ActivatedRoute,
        private readonly precisionService: PrecisionService
    ) {
        this.helpLink = this.ciraHelpService.getHelpLinkByKey(CiraHelpKeyConstants.cbAPWFManageVendorEdit);
        this.selectedCommunity = new CommunitySelectorResultDto();
        this.isInternalUser = this.userService.isInternal();
        this.communityColumns = this.communityService.getCommunitySelectorColumns();
        this.currencyFormat = this.precisionService.updatePrecisionPoint(StringFormatConstants.currencyFormat);

        this.route.queryParamMap.subscribe((qp) => {
            const uuid = qp.get('uuid');
            if (Utils.notNullAndDefined(uuid)) {
                const filters = localStorage.getItem(uuid);
                if (Utils.notNullAndDefined(filters)) {
                    const params = JSON.parse(filters);
                    this.communityId = params.communityId;
                    this.isLiteMode = params.liteMode;
                    this.getVendorEditQueueData();
                    localStorage.removeItem(uuid);
                }
                window.history.replaceState({}, document.title, window.location.href.split('?')[0]);
            }
        });
    }

    ngOnInit() {
        this.workflowVendorEditParams = new WorkflowVendorEditParametersDto();
        this.getActionOptions();
        this.getSelectedContext();
        this.contextManagerSubscription = this.contextManagerService.getContext().subscribe(() => {
            this.getSelectedContext();
        });
    }

    ngOnDestroy() {
        this.contextManagerSubscription.unsubscribe();
    }

    onRefreshClick(): void {
        this.getVendorEditQueueData(true);
    }

    onLiteModeValueChange(): void {
        this.liteModePreference.show(this.isLiteMode);
    }

    setStoredLiteMode(flag: boolean): void {
        setTimeout(() => {
            this.isLiteMode = flag;
            this.getVendorEditQueueData(true);
        }, 200);
    }

    onActionClick(event: ActionMenuEvent<WorkflowVendorEditResultDto>): void {
        if (Utils.equalsIgnoreCase(event.action, ActionConstants.EDIT)) {
            this.onEditClick(event.record);
        } else if (Utils.equalsIgnoreCase(event.action, ActionConstants.DELETE)) {
            this.selectedWorkflowVendorEditRow = event.record;
            this.showDeleteWorkflowPopUp = true;
        }
    }

    showUsersList(record: WorkflowVendorEditResultDto): void {
        this.selectedWorkflowVendorEditRow = record;
        this.showUsersListPopup = true;
    }

    onDownloadFile(record: WorkflowVendorEditResultDto): void {
        const baseParams = new WorkFlowParametersDto();
        baseParams.workflowId = record.workflowId;
        baseParams.communityId = record.communityId;

        this.downloadInvoice(baseParams);
    }

    onWorkflowNotesClick(record: WorkflowVendorEditResultDto): void {
        this.selectedWorkflowVendorEditRow = record;
        this.showWorkFlowNotesPopUp = true;
    }

    onWorkflowNotesPopClose(event): void {
        this.showWorkFlowNotesPopUp = false;
        if (Utils.notNullAndDefined(event)) {
            this.getVendorEditQueueData(true);
        }
    }

    onWorkflowHistoryClick(record: WorkflowVendorEditResultDto): void {
        this.selectedWorkflowVendorEditRow = record;
        this.showWorkFlowHistoryPopUp = true;
    }

    onWorkflowHistoryClose(): void {
        this.showWorkFlowHistoryPopUp = false;
    }

    onBoardHistoryClick(record: WorkflowVendorEditResultDto): void {
        this.selectedWorkflowVendorEditRow = record;
        this.showWorkFlowBoardHistoryPopUp = true;
    }

    onWorkflowBoardHistoryClose(): void {
        this.showWorkFlowBoardHistoryPopUp = false;
    }

    onBoardNotesClick(record: WorkflowVendorEditResultDto): void {
        this.selectedWorkflowVendorEditRow = record;
        this.showWorkFlowBoardNotesPopUp = true;
    }

    onWorkdlowBoardNotesPopClose(event): void {
        this.showWorkFlowBoardNotesPopUp = false;
        if (Utils.notNullAndDefined(event)) {
            this.getVendorEditQueueData(true);
        }
    }

    onCloseWorkflowVendorEditor(event): void {
        this.showWorkflowVendorEdit = false;
        if (Utils.notNullAndDefined(event)) {
            this.getVendorEditQueueData(true);
        }
    }

    onCommunitySelection(selectedObject): void {
        if (Utils.notNullAndDefined(selectedObject) && Utils.notNullOrEmpty(selectedObject.selectedRow)) {
            const selectedRows = selectedObject.selectedRow;
            this.communityId = selectedRows[0].communityId;
        } else {
            this.communityId = null;
        }

        this.getVendorEditQueueData();
    }

    onDeleteWorkflow(reason: string): void {
        this.deleteWorkflow(reason);
    }

    onDeleteWorkflowClose(): void {
        this.showDeleteWorkflowPopUp = false;
    }

    openPageInWindow(): void {
        const pageUrl = window.location.href.split('?')[0];
        const uuid = Utils.getUUID();
        localStorage.setItem(uuid, JSON.stringify(this.workflowVendorEditParams));
        window.open(`${pageUrl}?uuid=${uuid}`, '_blank').focus();
    }

    onLiteModePreferenceChange(): void {
        this.getVendorEditQueueData();
    }

    private deleteWorkflow(deleteReason: string): void {
        const baseParams: WFEDeleteWorkflowParametersDto = new WFEDeleteWorkflowParametersDto();
        baseParams.workflowId = this.selectedWorkflowVendorEditRow.workflowId;
        baseParams.communityId = this.selectedWorkflowVendorEditRow.communityId;
        baseParams.workflowStatus = this.selectedWorkflowVendorEditRow.workflowStatus;
        baseParams.deleteReason = deleteReason;

        this.loadingVisible = true;
        this.workflowEditorService.deleteWorkflow(baseParams).subscribe(
            (result: StatusMessageResultDto) => {
                if (Utils.isTrue(result.isSuccessMessage)) {
                    notify(result.message, 'Success', MessageInterval.successMsgInterval);
                    this.getVendorEditQueueData(true);
                } else {
                    notify(result.message, 'Error', MessageInterval.errorMsgInterval);
                }
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private onEditClick(record: WorkflowVendorEditResultDto): void {
        this.selectedWorkflowVendorEditRow = record;
        this.showWorkflowVendorEdit = true;
    }

    private getVendorEditQueueData(clearCache = false): void {
        const baseParam: WorkflowVendorEditParametersDto = new WorkflowVendorEditParametersDto();
        baseParam.clearCache = clearCache;
        baseParam.liteMode = this.isLiteMode;
        if (Utils.notNullAndDefined(this.communityId)) {
            baseParam.communityId = this.communityId;
        }
        this.workflowVendorEditParams = baseParam;
        this.loadingVisible = true;
        this.wfVendorEditService.getVendorEditQueue(baseParam).subscribe(
            (result: WorkflowVendorEditResultDto[]) => {
                this.gridSource = result;
                this.prepareUniqueResponsibleUsers();
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private downloadInvoice(baseParams: WorkFlowParametersDto): void {
        this.loadingVisible = true;
        this.workflowSharedService.downloadInvoice(baseParams).subscribe(
            (result: Blob) => {
                const fileURL: string = this.helperService.getFileUrlFromBlob(result);
                window.open(fileURL, '_blank', 'location=yes, scrollbars=yes,status=yes');
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private getCommunityList() {
        this.communityService.getCommunities().subscribe(
            (data) => {
                this.communityList = data;
                this.communityGridBoxValue = this.communityId ? [this.communityId] : [];
                this.getVendorEditQueueData();
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private getActionOptions(): void {
        this.actionMenuItems = [
            {
                action: ActionConstants.EDIT,
                icon: this.iconEdit,
                name: 'Edit'
            },
            {
                action: ActionConstants.DELETE,
                icon: this.iconDelete,
                name: 'Delete'
            }
        ];
    }

    private getSelectedContext(): void {
        this.selectedContext = this.contextManagerService.getSelectedContext();
        if (
            Utils.notNullAndDefined(this.selectedContext) &&
            Utils.notNullAndDefined(this.selectedContext.communityId)
        ) {
            this.communityId = this.selectedContext.communityId;
            this.getVendorEditQueueData();
        } else {
            this.getCommunityList();
        }
    }

    private prepareUniqueResponsibleUsers(): void {
        this.uniqueResponsibleUsers = this.helperService.prepareUniqueResponsibleUsers(
            this.gridSource,
            'dashboardResponsibleUsers'
        );
    }
}
